import { flexRender, Table } from '@tanstack/react-table'
import { cn } from '@/lib/utils'

interface DataTableNewProps<TData> {
  /**
   * TanStack Table instance
   */
  table: Table<TData>
  /**
   * Optional: table container height
   */
  height?: string
  /**
   * Optional: additional className
   */
  className?: string
  /**
   * Optional: callback when a row is clicked
   */
  onRowClick?: (_row: TData) => void
}

/**
 * DataTableNew - Reusable table component
 *
 * Features:
 * - Sticky header with vertical scroll
 * - Auto-sizing columns based on content
 * - Supports fixed size (size), minimum (minSize), and maximum (maxSize) widths
 * - Adjustable height to prevent system scroll
 * - noWrap meta property to auto-fit content without wrapping
 *
 * Column configuration:
 * - size: fixed width in pixels (e.g., 200)
 * - minSize: minimum width in pixels (e.g., 150)
 * - maxSize: maximum width in pixels (e.g., 400)
 * - meta.noWrap: true to auto-fit content without line wrapping (width: 1%)
 * - No configuration: auto-fits to largest content
 *
 * @example
 * ```tsx
 * const columns = [
 *   {
 *     accessorKey: 'name',
 *     header: 'Name',
 *     minSize: 150,
 *   },
 *   {
 *     accessorKey: 'status',
 *     header: 'Status',
 *     meta: { noWrap: true },
 *   },
 *   {
 *     accessorKey: 'description',
 *     header: 'Description',
 *     minSize: 200,
 *     maxSize: 400,
 *   },
 * ]
 *
 * <DataTableNew table={table} height="600px" />
 * ```
 */
export function DataTableNew<TData>({
  table,
  height = '776px',
  className,
  onRowClick,
}: DataTableNewProps<TData>) {
  return (
    <div
      className={cn(
        'flex flex-col overflow-hidden rounded-md border border-neutral-300 bg-[#FAFAFA]',
        className
      )}
      style={{ height, width: '100%' }}
    >
      <div className="h-full w-full overflow-auto">
        <table
          className="w-full border-collapse font-geist"
          style={{ tableLayout: 'auto' }}
        >
          <thead
            className="sticky top-0 z-[1] bg-[#FAFAFA]"
            style={{ boxShadow: '0 1px 0 0 #D4D4D4' }}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  const columnDef = header.column.columnDef as any
                  const size = columnDef.size
                  const minSize = columnDef.minSize
                  const maxSize = columnDef.maxSize
                  const noWrap = columnDef.meta?.noWrap ?? false

                  let widthStyle: React.CSSProperties = {}

                  if (noWrap) {
                    widthStyle = { width: '1%' }
                  } else if (size) {
                    widthStyle = {
                      width: `${size}px`,
                      minWidth: `${size}px`,
                      maxWidth: `${size}px`,
                    }
                  } else {
                    widthStyle = {
                      minWidth: minSize ? `${minSize}px` : undefined,
                      maxWidth: maxSize ? `${maxSize}px` : undefined,
                    }
                  }

                  return (
                    <th
                      className={cn(
                        'h-[44px] min-h-[44px]',
                        'bg-[#FAFAFA] text-left',
                        'font-geist text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]',
                        noWrap
                          ? 'whitespace-nowrap px-2 py-2 pr-8'
                          : 'px-2 py-2'
                      )}
                      key={header.id}
                      style={{
                        ...widthStyle,
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>

          <tbody className="bg-[#FAFAFA]">
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  className="px-2 py-8 text-center font-geist text-base font-medium leading-6 text-[#262626]"
                  colSpan={table.getAllColumns().length}
                >
                  No hay datos disponibles
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  className={cn(
                    'border border-b',
                    onRowClick && 'cursor-pointer',
                    'transition-colors duration-200 hover:bg-[#F0F0F0]'
                  )}
                  key={row.id}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                  {row.getVisibleCells().map((cell: any) => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const columnDef = cell.column.columnDef as any
                    const size = columnDef.size
                    const minSize = columnDef.minSize
                    const maxSize = columnDef.maxSize
                    const noWrap = columnDef.meta?.noWrap ?? false

                    let widthStyle: React.CSSProperties = {}

                    if (noWrap) {
                      widthStyle = { width: '0px' }
                    } else if (size) {
                      widthStyle = {
                        width: `${size}px`,
                        minWidth: `${size}px`,
                        maxWidth: `${size}px`,
                      }
                    } else {
                      widthStyle = {
                        minWidth: minSize ? `${minSize}px` : undefined,
                        maxWidth: maxSize ? `${maxSize}px` : undefined,
                      }
                    }

                    return (
                      <td
                        className={cn(
                          'h-12 bg-transparent',
                          'text-left font-geist text-sm font-normal leading-5 tracking-[0.005em] text-[#262626]',
                          noWrap
                            ? 'whitespace-nowrap px-2 py-2 pr-8'
                            : 'px-2 py-2'
                        )}
                        key={cell.id}
                        style={{
                          ...widthStyle,
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
